import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { en } from './locales/en';
import { tr } from './locales/tr';

/**
 * The panel's translation layer.
 *
 * Deliberately about sixty lines rather than a dependency: two flat
 * dictionaries, a lookup with `{placeholder}` substitution, and a context so a
 * switch re-renders the tree. Turkish is the default because that is the
 * language the people running this console work in; English stays because the
 * copy is the source of truth for what every screen means.
 */

export type Language = 'tr' | 'en';

export const LANGUAGES: { value: Language; label: string }[] = [
  { value: 'tr', label: 'Türkçe' },
  { value: 'en', label: 'English' },
];

const DEFAULT_LANGUAGE: Language = 'tr';

const STORAGE_KEY = 'alarm.lang';

const DICTIONARIES: Record<Language, Record<string, string>> = { tr, en };

/** What `Intl` should be handed for each language. */
const LOCALES: Record<Language, string> = { tr: 'tr-TR', en: 'en-GB' };

export type Vars = Record<string, string | number>;
export type Translate = (key: string, vars?: Vars) => string;

/**
 * Module-level rather than context-only, so `format.ts` can localise a number
 * without every formatting helper having to become a hook. The provider keeps
 * it in step with the state React renders from.
 */
let currentLanguage: Language = readStoredLanguage();

function readStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'tr' || stored === 'en') return stored;
  } catch {
    // A browser with storage blocked still gets a working console.
  }
  return DEFAULT_LANGUAGE;
}

export function currentLocale(): string {
  return LOCALES[currentLanguage];
}

/**
 * Looks a key up, falling back to English and then to the key itself — a
 * missing translation shows the English copy rather than an empty element.
 *
 * `count` also selects an optional `<key>_one` entry, which is how English
 * gets its singular. Turkish does not inflect a noun after a number, so its
 * dictionary simply omits those and falls through to the shared form.
 */
export function translate(key: string, vars?: Vars): string {
  const dictionary = DICTIONARIES[currentLanguage];
  // `'1'` as well as `1`, so a caller may pass an already-formatted count and
  // still get the singular.
  const singular = vars?.count === 1 || vars?.count === '1';

  // The active language is exhausted — singular *and* shared form — before
  // English is consulted at all. Falling back per-form instead would hand a
  // Turkish screen the English singular every time a count came out as one,
  // since Turkish deliberately has no `_one` entries.
  const template =
    (singular ? dictionary[`${key}_one`] : undefined) ??
    dictionary[key] ??
    (singular ? en[`${key}_one`] : undefined) ??
    en[key] ??
    key;

  return vars ? fill(template, vars) : template;
}

function fill(template: string, vars: Vars): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

interface I18n {
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translate;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(currentLanguage);

  const setLanguage = useCallback((next: Language) => {
    currentLanguage = next;
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is not a reason to refuse it.
    }
    setLanguageState(next);
  }, []);

  // `language` is in the dependency list precisely because `translate` reads
  // module state rather than closing over it: a new `t` identity is what tells
  // React the strings below this provider are now different.
  const value = useMemo<I18n>(
    () => ({ language, setLanguage, t: (key, vars) => translate(key, vars) }),
    [language, setLanguage],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside an I18nProvider');
  return value;
}

/** The common case: a component that only needs to translate. */
export function useT(): Translate {
  return useI18n().t;
}
